import { useState, useEffect } from 'react';
import { Sparkles, Target, Palette, Send, ArrowRight } from 'lucide-react';

const cards = [
  {
    id: 'onboarding',
    tag: 'STEP 1',
    title: 'Business DNA',
    subtitle: 'Set up in minutes',
    gridColor: '#2563eb',
    accentColor: 'blue',
    icon: Sparkles,
    features: [
      'Business name & brand position',
      'Ideal customer persona',
      'Competitor & market focus',
      'AI reads your full profile'
    ],
    metric: { value: '5 MIN', label: 'SETUP' }
  },
  {
    id: 'strategy',
    tag: 'STEP 2',
    title: 'AI Strategy',
    subtitle: 'Generated in seconds',
    gridColor: '#16a34a',
    accentColor: 'green',
    icon: Target,
    features: [
      'SMART goal generation',
      'Brand positioning statement',
      'Channel mix (60/20/20)',
      '30-day content calendar'
    ],
    metric: { value: '30', label: 'DAYS' }
  },
  {
    id: 'calendar',
    tag: 'STEP 3',
    title: 'Design & Create',
    subtitle: '12 templates · 14 text styles',
    gridColor: '#2563eb',
    accentColor: 'blue',
    icon: Palette,
    features: [
      'AI captions & hashtags per post',
      'Design Studio (Canva-style)',
      'AI image generation (6 styles)',
      'Guided video workflow (4-step)'
    ],
    metric: { value: '12', label: 'TEMPLATES' }
  },
  {
    id: 'results',
    tag: 'STEP 4',
    title: 'Publish & Track',
    subtitle: 'Direct to Instagram & Facebook',
    gridColor: '#16a34a',
    accentColor: 'green',
    icon: Send,
    features: [
      'One-click social publishing',
      'Campaign progress dashboard',
      'Posting streak tracking',
      'PDF campaign report download'
    ],
    metric: { value: 'LIVE', label: 'PUBLISH' }
  }
];

export default function HeroWithBento({ onPanelOpen }) {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setIsVisible(true), 50);
    return () => clearTimeout(timer);
  }, []);

  return (
    <div className="min-h-screen px-4 md:px-6 pt-28 pb-16" style={{ background: '#060e1e' }}>
      <div className="max-w-7xl mx-auto">

        {/* Section badge */}
        <div className="flex justify-center mb-8">
          <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-slate-700 bg-slate-800/60 text-xs text-slate-400 font-medium">
            <div className="w-1.5 h-1.5 bg-blue-500 rounded-full animate-pulse" />
            Interactive Platform Demo — Click any card to explore
          </div>
        </div>

        {/* Hero heading */}
        <div
          className="text-center mb-10 md:mb-14 transition-all duration-700"
          style={{ opacity: isVisible ? 1 : 0, transform: isVisible ? 'translateY(0)' : 'translateY(24px)' }}
        >
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-extrabold text-white leading-[1.08] mb-4">
            From business brief<br />
            <span style={{
              backgroundImage: 'linear-gradient(90deg,#60a5fa 0%,#34d399 100%)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              backgroundClip: 'text'
            }}>
              to published post.
            </span>
          </h1>
          <p className="text-sm md:text-base text-slate-400 max-w-xl mx-auto leading-relaxed">
            Digital Trent guides you through every step — strategy, design, content creation,
            and direct social publishing — all in one platform.
          </p>
        </div>

        {/* 4-card bento grid */}
        <div
          className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 transition-all duration-700"
          style={{
            opacity: isVisible ? 1 : 0,
            transform: isVisible ? 'translateY(0)' : 'translateY(32px)',
            transitionDelay: '150ms'
          }}
        >
          {cards.map((card) => {
            const Icon = card.icon;
            const isBlue = card.accentColor === 'blue';
            const accentBg = isBlue ? 'bg-blue-600' : 'bg-green-600';
            const accentText = isBlue ? 'text-blue-400' : 'text-green-400';
            const hoverBorder = isBlue ? 'hover:border-blue-500/50' : 'hover:border-green-500/50';

            return (
              <div
                key={card.id}
                onClick={() => onPanelOpen(card.id)}
                className={`relative bg-slate-800/90 backdrop-blur-sm rounded-xl overflow-hidden border border-slate-700/60 ${hoverBorder} cursor-pointer group transition-all duration-300 hover:-translate-y-1 hover:shadow-2xl hover:bg-slate-800`}
              >
                {/* Micro grid — top-left corner */}
                <div className="absolute top-0 left-0 w-20 h-20 pointer-events-none opacity-30 overflow-hidden">
                  <div
                    style={{
                      backgroundImage: `
                        linear-gradient(${card.gridColor} 0.5px, transparent 0.5px),
                        linear-gradient(90deg, ${card.gridColor} 0.5px, transparent 0.5px)
                      `,
                      backgroundSize: '8px 8px'
                    }}
                    className="absolute inset-0"
                  />
                  <div
                    style={{ background: 'linear-gradient(135deg, transparent 0%, rgba(6,14,30,0.9) 60%)' }}
                    className="absolute inset-0"
                  />
                </div>

                {/* Dark header strip */}
                <div
                  className="relative h-9 overflow-hidden flex items-center justify-between px-3"
                  style={{ backgroundColor: '#0a1526' }}
                >
                  <span className="text-[10px] font-bold text-white/50 uppercase tracking-widest">
                    {card.tag}
                  </span>
                  <span className={`text-[10px] font-bold ${accentText} uppercase tracking-wide`}>
                    {card.metric.value} {card.metric.label}
                  </span>
                </div>

                {/* Content */}
                <div className="p-4">

                  {/* Icon + title */}
                  <div className="flex items-start gap-3 mb-3">
                    <div className={`${accentBg} p-2 rounded-lg flex-shrink-0`}>
                      <Icon className="w-4 h-4 text-white" strokeWidth={2.5} />
                    </div>
                    <div>
                      <h3 className="text-sm font-bold text-white leading-tight">{card.title}</h3>
                      <p className="text-[11px] text-slate-500 mt-0.5 leading-tight">{card.subtitle}</p>
                    </div>
                  </div>

                  {/* Feature list */}
                  <ul className="space-y-1.5 mb-4">
                    {card.features.map((feat, i) => (
                      <li key={i} className="flex items-start gap-2 text-[11px] text-slate-400 leading-tight">
                        <span className={`${accentText} font-bold flex-shrink-0 mt-0.5`}>✓</span>
                        <span>{feat}</span>
                      </li>
                    ))}
                  </ul>

                  {/* CTA */}
                  <div className={`flex items-center gap-1.5 text-xs font-semibold ${accentText} group-hover:gap-2.5 transition-all duration-200`}>
                    <span>Open Demo</span>
                    <ArrowRight className="w-3 h-3 transition-transform duration-200 group-hover:translate-x-0.5" />
                  </div>

                </div>
              </div>
            );
          })}
        </div>

        {/* Trust signals */}
        <div
          className="mt-10 flex flex-wrap justify-center gap-6 md:gap-10 transition-all duration-700"
          style={{ opacity: isVisible ? 1 : 0, transitionDelay: '300ms' }}
        >
          {[
            '14-day free trial',
            'No credit card required',
            'Cancel anytime'
          ].map((label, i) => (
            <div key={i} className="flex items-center gap-2 text-xs text-slate-500">
              <span className="text-green-500 font-bold">✓</span>
              <span>{label}</span>
            </div>
          ))}
        </div>

      </div>
    </div>
  );
}
